"use client";

import { useRef } from "react";
import Link from "next/link";
import CdnImage from "@/components/CdnImage";
import { ChevronLeft, ChevronRight, ArrowRight } from "lucide-react";
import { useT } from "@/hooks/useT";

type BlogPost = {
  title: string;
  excerpt: string;
  date: string;
  slug: string;
  cover: string | null;
  tags: string[];
};

export default function BlogCarouselSection() {
  const t = useT();
  const scrollRef = useRef<HTMLDivElement>(null);

  const BLOG_POSTS: BlogPost[] = [
    {
      title: t("blogCarousel.post1.title"),
      excerpt: t("blogCarousel.post1.excerpt"),
      date: "2024-10-28",
      slug: "what-is-polymarket",
      cover: "/capturas/polymarket-dashboard.png",
      tags: ["Polymarket", t("blogCarousel.tag.predictionMarkets"), t("blogCarousel.tag.cryptoTrading")],
    },
    {
      title: t("blogCarousel.post2.title"),
      excerpt: t("blogCarousel.post2.excerpt"),
      date: "2025-11-21",
      slug: "earn-money-sharing-polymarket-predictions",
      cover: "/blog/earn-with-predictions.webp",
      tags: ["Polymarket", t("blogCarousel.tag.earnMoney"), t("blogCarousel.tag.trading")],
    },
    {
      title: t("blogCarousel.post3.title"),
      excerpt: t("blogCarousel.post3.excerpt"),
      date: "2025-11-19",
      slug: "polymarket-arbitrage-strategies",
      cover: "/blog/arbitrage-strategies.webp",
      tags: ["Polymarket", t("blogCarousel.tag.tradingStrategies"), t("blogCarousel.tag.arbitrage")],
    },
  ];

  const scroll = (direction: "left" | "right") => {
    if (scrollRef.current) {
      const scrollAmount = 340;
      scrollRef.current.scrollBy({
        left: direction === "left" ? -scrollAmount : scrollAmount,
        behavior: "smooth",
      });
    }
  };

  return (
    <section className="bg-white dark:bg-[#0f0f12] py-16 sm:py-24 px-4 sm:px-8 lg:px-16">
      <div className="max-w-7xl mx-auto">
        {/* Header */}
        <div className="flex flex-col sm:flex-row sm:items-end sm:justify-between gap-4 mb-10">
          <div>
            <p className="text-sm font-monument font-semibold text-gray-500 dark:text-gray-400 uppercase tracking-wider mb-3">
              {t("blogCarousel.eyebrow")}
            </p>
            <h2 className="text-3xl sm:text-4xl font-monument font-bold text-gray-900 dark:text-white">
              {t("blogCarousel.headline.before")}{" "}
              <span className="text-accent-primary">{t("blogCarousel.headline.accent")}</span>
            </h2>
            <p className="text-gray-500 dark:text-gray-400 font-satoshi mt-2 max-w-lg">
              {t("blogCarousel.subtitle")}
            </p>
          </div>

          <div className="flex items-center gap-4">
            {/* Navigation arrows */}
            <div className="hidden sm:flex items-center gap-2">
              <button
                onClick={() => scroll("left")}
                className="p-2 rounded-[5px] border border-gray-200 dark:border-[#1A1A1A] hover:bg-gray-50 dark:hover:bg-[#1e1e24] transition-colors"
                aria-label="Scroll left"
              >
                <ChevronLeft className="w-5 h-5 text-gray-500 dark:text-gray-400" />
              </button>
              <button
                onClick={() => scroll("right")}
                className="p-2 rounded-[5px] border border-gray-200 dark:border-[#1A1A1A] hover:bg-gray-50 dark:hover:bg-[#1e1e24] transition-colors"
                aria-label="Scroll right"
              >
                <ChevronRight className="w-5 h-5 text-gray-500 dark:text-gray-400" />
              </button>
            </div>

            <Link
              href="/blog"
              prefetch={false}
              className="inline-flex items-center gap-2 text-sm font-inter-tight font-semibold text-gray-900 dark:text-white hover:text-gray-600 dark:hover:text-gray-300 transition-colors"
            >
              {t("blogCarousel.viewAll")}
              <ArrowRight className="w-4 h-4" />
            </Link>
          </div>
        </div>

        {/* Carousel */}
        <div
          ref={scrollRef}
          className="flex gap-6 overflow-x-auto pb-4 snap-x snap-mandatory scrollbar-hide"
          style={{ scrollbarWidth: "none", msOverflowStyle: "none" }}
        >
          {BLOG_POSTS.map((post) => (
            <article
              key={post.slug}
              className="flex-shrink-0 w-[320px] sm:w-[340px] snap-start"
            >
              <Link
                href={`/blog/${post.slug}`}
                className="group block bg-white dark:bg-[#18181D] rounded-[5px] p-4 border border-gray-200 dark:border-[#1A1A1A] hover:bg-gray-50 dark:hover:bg-[#1e1e24] transition-colors"
              >
                <div className="relative h-[180px] rounded-[5px] overflow-hidden mb-4 bg-gray-100 dark:bg-[#0f0f12]">
                  {post.cover ? (
                    <CdnImage
                      src={post.cover}
                      alt={post.title}
                      fill
                      className="object-cover"
                    />
                  ) : (
                    <div className="absolute inset-0 bg-gray-100 dark:bg-[#0f0f12]" />
                  )}
                </div>

                {/* Tags */}
                <div className="flex flex-wrap gap-2 mb-3">
                  {post.tags.slice(0, 2).map((tag) => (
                    <span
                      key={tag}
                      className="px-2 py-1 text-xs font-inter-tight font-medium rounded-[5px] bg-gray-100 dark:bg-[#0f0f12] text-gray-600 dark:text-gray-400"
                    >
                      {tag}
                    </span>
                  ))}
                </div>

                {/* Title */}
                <h3 className="text-lg font-monument font-bold text-gray-900 dark:text-white mb-2 line-clamp-2">
                  {post.title}
                </h3>

                {/* Excerpt */}
                <p className="text-sm font-satoshi text-gray-500 dark:text-gray-400 line-clamp-2 mb-3">
                  {post.excerpt}
                </p>

                {/* Date & CTA */}
                <div className="flex items-center justify-between">
                  <span className="text-xs font-satoshi text-gray-500 dark:text-gray-500">
                    {new Date(post.date).toLocaleDateString("en-US", {
                      month: "short",
                      day: "numeric",
                      year: "numeric",
                    })}
                  </span>
                  <span className="text-xs font-inter-tight font-semibold text-gray-900 dark:text-white">
                    {t("blogCarousel.readMore")} &rarr;
                  </span>
                </div>
              </Link>
            </article>
          ))}
        </div>
      </div>

      <style jsx>{`
        .scrollbar-hide::-webkit-scrollbar {
          display: none;
        }
      `}</style>
    </section>
  );
}
